// frontend/packages/business-console/src/pages/Invoices.jsx
//
// Work item 2.16 — Invoices list page (`/business/:slug/invoices`).
// Spec source: `docs/design/quotes-invoices-console.md` §3.3.
//
// Mirror of `Quotes.jsx` (2.13) with:
//   - State chip set extended for invoice machine (paid/partial/overdue/void)
//   - **Summary chip strip** above the table — "Outstanding: $X across Y
//     · Overdue: $Z". Vocabulary matches the Pulse Money section so
//     operators don't relearn terms when they switch pages.
//   - Money columns include amountPaid + amountDue for partial-payment
//     visibility on the row (paid invoices collapse to total only).

import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import {
  Link as RouterLink,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';

import { BusinessContext } from '../contexts/BusinessContext';
import { useInvoicesList, summarizeInvoices } from './Invoices/useInvoicesList';
import {
  useTokens,
  Surface,
  StatusPill,
  PageHeader,
  MetricBarCards,
} from '../components/primitives';

const STATE_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'draft', label: 'Drafts' },
  { key: 'sent', label: 'Sent' },
  { key: 'viewed', label: 'Viewed' },
  { key: 'partial', label: 'Partial' },
  { key: 'paid', label: 'Paid' },
  { key: 'overdue', label: 'Overdue' },
  { key: 'void', label: 'Void' },
];

// Invoice state -> shared StatusPill kind (semantic color).
const STATE_KIND = {
  draft: 'neutral',
  sent: 'info',
  viewed: 'warning',
  partial: 'warning',
  paid: 'success',
  overdue: 'error',
  void: 'neutral',
};

function StatePill({ state }) {
  return (
    <StatusPill
      size="sm"
      kind={STATE_KIND[state] || 'neutral'}
      label={state}
      sx={{ textTransform: 'capitalize' }}
    />
  );
}

function fmtUsd(cents) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format((cents || 0) / 100);
}

function fmtUsdCompact(cents) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format((cents || 0) / 100);
}

function formatRelative(input) {
  if (!input) return '—';
  const d = new Date(input);
  if (Number.isNaN(d.getTime())) return '—';
  const days = Math.round((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24));
  if (days < 1) return 'today';
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.round(days / 7)}w ago`;
  return d.toLocaleDateString();
}

function formatDue(input) {
  if (!input) return '—';
  const d = new Date(input);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export default function Invoices() {
  const { business } = useContext(BusinessContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const basePath = business?.slug ? `/business/${business.slug}` : '';

  // See Quotes.jsx for the rationale on URL filter hydration.
  const [searchParams, setSearchParams] = useSearchParams();
  const initialState = searchParams.get('state');
  const initialQ = searchParams.get('q') || '';
  const allowedStates = useMemo(
    () => new Set(STATE_FILTERS.map((f) => f.key)),
    []
  );
  const [stateFilter, setStateFilter] = useState(
    initialState && allowedStates.has(initialState) ? initialState : 'all'
  );
  const [search, setSearch] = useState(initialQ);
  const [debouncedSearch, setDebouncedSearch] = useState(initialQ);

  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (stateFilter === 'all') next.delete('state');
    else next.set('state', stateFilter);
    if (!debouncedSearch) next.delete('q');
    else next.set('q', debouncedSearch);
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
  }, [stateFilter, debouncedSearch, searchParams, setSearchParams]);

  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(id);
  }, [search]);

  const {
    items,
    hasMore,
    loading,
    loadingMore,
    error,
    loadMore,
    refresh,
    isDemo,
  } = useInvoicesList({ state: stateFilter, q: debouncedSearch });

  const summary = useMemo(() => summarizeInvoices(items), [items]);

  // Design stat strip: Draft / Sent / Overdue / Paid + gradient Outstanding.
  // Outstanding + overdue dollars are real (summarizeInvoices); the state
  // counts are derived from the loaded list (exact in demo; a tenant-wide
  // count aggregate is a follow-up for real mode).
  const statCards = useMemo(() => {
    if (!items.length) return [];
    const count = (states) =>
      items.filter((i) => states.includes(i.state)).length;
    return [
      { label: 'Draft', value: count(['draft']), bar: 'grayBorder' },
      {
        label: 'Sent',
        value: count(['sent', 'viewed', 'partial']),
        bar: 'blue',
      },
      { label: 'Overdue', value: summary.overdueCount || 0, bar: 'red' },
      { label: 'Paid', value: count(['paid']), bar: 'green' },
      {
        label: 'Outstanding',
        value: fmtUsdCompact(summary.outstandingCents),
        sub:
          summary.overdueCents > 0
            ? `${fmtUsdCompact(summary.overdueCents)} overdue`
            : 'all current',
        gradient: true,
      },
    ];
  }, [items, summary]);
  const hasFilterActive = stateFilter !== 'all' || debouncedSearch.length > 0;

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 3 },
        maxWidth: 1280,
        mx: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      {/* ── Header ──────────────────────────────────────────────── */}
      <PageHeader
        title="Invoices"
        meta={
          <Typography
            sx={{ fontSize: 13, color: 'text.secondary', fontWeight: 600 }}
          >
            Money owed to you and the payments coming in.
            {isDemo ? '  ·  Demo mode' : ''}
          </Typography>
        }
        actions={
          <Stack direction="row" spacing={1} alignItems="center">
            <Tooltip title="Refresh">
              <IconButton
                size="small"
                onClick={refresh}
                disabled={loading}
                aria-label="Refresh invoices"
              >
                <RefreshRoundedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Button
              size="small"
              component={RouterLink}
              to={`${basePath}/invoices/drafts`}
              sx={{ textTransform: 'none' }}
            >
              Offline drafts
            </Button>
            <Button
              variant="contained"
              startIcon={<AddRoundedIcon />}
              component={RouterLink}
              to={`${basePath}/invoices/new`}
            >
              New invoice
            </Button>
          </Stack>
        }
      />

      {/* ── Design stat strip ──── */}
      {!loading && statCards.length ? (
        <MetricBarCards cards={statCards} sx={{ mb: 1.5 }} />
      ) : null}

      {/* ── Filter chips + search ───────────────────────────────── */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        sx={{ alignItems: { xs: 'stretch', sm: 'center' } }}
      >
        <Stack
          direction="row"
          spacing={1}
          sx={{ flexWrap: 'wrap', gap: 1, rowGap: 1 }}
        >
          {STATE_FILTERS.map((f) => (
            <Chip
              key={f.key}
              label={f.label}
              size="small"
              clickable
              color={stateFilter === f.key ? 'primary' : 'default'}
              variant={stateFilter === f.key ? 'filled' : 'outlined'}
              onClick={() => setStateFilter(f.key)}
            />
          ))}
        </Stack>
        <Box sx={{ flex: 1 }} />
        <TextField
          size="small"
          placeholder="Search INV-number or customer..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: { xs: '100%', sm: 260 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchOutlinedIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Stack>

      {/* ── Error banner ────────────────────────────────────────── */}
      {error ? (
        <Alert severity="error" onClose={() => refresh()}>
          {error?.response?.data?.error ||
            error?.message ||
            "Couldn't load invoices."}
        </Alert>
      ) : null}

      {/* ── List body ───────────────────────────────────────────── */}
      {loading ? (
        <LoadingState />
      ) : items.length === 0 ? (
        <EmptyState hasFilterActive={hasFilterActive} basePath={basePath} />
      ) : isMobile ? (
        <InvoiceCardList
          items={items}
          basePath={basePath}
          navigate={navigate}
        />
      ) : (
        <InvoiceTable items={items} basePath={basePath} navigate={navigate} />
      )}

      {/* ── Pagination ──────────────────────────────────────────── */}
      {hasMore ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
          <Button
            variant="outlined"
            onClick={loadMore}
            disabled={loadingMore}
            startIcon={loadingMore ? <CircularProgress size={14} /> : null}
          >
            {loadingMore ? 'Loading…' : 'Load more'}
          </Button>
        </Box>
      ) : null}
    </Box>
  );
}

function LoadingState() {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
      <CircularProgress />
    </Box>
  );
}

function EmptyState({ hasFilterActive, basePath }) {
  return (
    <Surface
      sx={{
        borderStyle: 'dashed',
        py: 6,
        px: 3,
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 2,
      }}
    >
      <ReceiptLongOutlinedIcon
        sx={{ fontSize: 48, color: 'text.secondary', opacity: 0.6 }}
      />
      {hasFilterActive ? (
        <>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            No invoices match this filter
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: 'text.secondary', maxWidth: 380 }}
          >
            Try clearing the search or picking a different state.
          </Typography>
        </>
      ) : (
        <>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            No invoices yet
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: 'text.secondary', maxWidth: 380 }}
          >
            Invoices you create will appear here. Send one to a customer and the
            payment status will flow through Pulse and the CRM.
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddRoundedIcon />}
            component={RouterLink}
            to={`${basePath}/invoices/new`}
          >
            Create your first invoice
          </Button>
        </>
      )}
    </Surface>
  );
}

function InvoiceTable({ items, basePath, navigate }) {
  const t = useTokens();
  return (
    <TableContainer component={Surface} sx={{ overflow: 'hidden' }}>
      <Table
        size="small"
        sx={{
          '& th': {
            fontWeight: 800,
            fontSize: 10,
            letterSpacing: '.4px',
            textTransform: 'uppercase',
            color: t.faint,
            bgcolor: t.tableHead,
            borderBottom: `1px solid ${t.hairline}`,
          },
          '& td': { borderBottom: `1px solid ${t.hairline}`, color: t.text },
        }}
      >
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: 110 }}>Number</TableCell>
            <TableCell>Customer</TableCell>
            <TableCell sx={{ width: 110 }}>State</TableCell>
            <TableCell align="right" sx={{ width: 140 }}>
              Total
            </TableCell>
            <TableCell align="right" sx={{ width: 130 }}>
              Outstanding
            </TableCell>
            <TableCell sx={{ width: 110 }}>Due</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((inv) => (
            <TableRow
              key={inv._id}
              hover
              onClick={() => navigate(`${basePath}/invoices/${inv._id}`)}
              sx={{ cursor: 'pointer', '&:hover': { bgcolor: t.subtle } }}
            >
              <TableCell sx={{ fontWeight: 700, color: t.blue }}>
                {inv.number}
              </TableCell>
              <TableCell>
                <Typography
                  sx={{ fontSize: 13, fontWeight: 700, color: t.text }}
                >
                  {inv.customerName || '—'}
                </Typography>
                {inv.title ? (
                  <Typography sx={{ fontSize: 11.5, color: t.muted }}>
                    {inv.title}
                  </Typography>
                ) : null}
              </TableCell>
              <TableCell>
                <StatePill state={inv.state} />
              </TableCell>
              <TableCell
                align="right"
                sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 800 }}
              >
                {fmtUsd(inv.total)}
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  fontVariantNumeric: 'tabular-nums',
                  color:
                    inv.state === 'paid'
                      ? t.greenText
                      : inv.state === 'overdue'
                        ? t.red
                        : t.text,
                  fontWeight: 700,
                }}
              >
                {inv.state === 'paid' ? '—' : fmtUsd(inv.amountDue)}
              </TableCell>
              <TableCell sx={{ color: t.faint }}>
                {formatDue(inv.dueAt)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function InvoiceCardList({ items, basePath, navigate }) {
  return (
    <Stack spacing={1.5}>
      {items.map((inv) => (
        <Surface
          key={inv._id}
          interactive
          onClick={() => navigate(`${basePath}/invoices/${inv._id}`)}
          sx={{ p: 2 }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              gap: 1,
            }}
          >
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                variant="caption"
                sx={{
                  fontFamily: 'monospace',
                  color: 'text.secondary',
                  fontWeight: 600,
                }}
              >
                {inv.number}
              </Typography>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  mt: 0.25,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {inv.customerName || '—'}
              </Typography>
              {inv.title ? (
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    display: 'block',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {inv.title}
                </Typography>
              ) : null}
            </Box>
            <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 700,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {fmtUsd(inv.total)}
              </Typography>
              {inv.state !== 'paid' && inv.amountDue > 0 ? (
                <Typography
                  variant="caption"
                  sx={{
                    color:
                      inv.state === 'overdue' ? 'error.main' : 'text.secondary',
                    fontVariantNumeric: 'tabular-nums',
                    display: 'block',
                  }}
                >
                  {fmtUsd(inv.amountDue)} due
                </Typography>
              ) : null}
              <Box
                sx={{ mt: 0.5, display: 'flex', justifyContent: 'flex-end' }}
              >
                <StatePill state={inv.state} />
              </Box>
            </Box>
          </Box>
          <Typography
            variant="caption"
            sx={{ color: 'text.secondary', mt: 1, display: 'block' }}
          >
            {inv.dueAt ? `Due ${formatDue(inv.dueAt)}` : 'No due date'}
            {'  ·  Created '}
            {formatRelative(inv.createdAt)}
          </Typography>
        </Surface>
      ))}
    </Stack>
  );
}
